import React, { useEffect } from "react";
import styled from "styled-components";
import { Alert } from "@mui/material";
import { useDispatch } from "react-redux";
import { LoaderSpinner2 } from "components";
import RulesTable from "pages/Products/components/RulesTable/RulesTable.component";
import { AppDispatch, useTypedSelector } from "store";
import { fetchTableIfNeededAction } from "store/reducers/products/tables/tables.actions";
import { TableAsyncStatus } from "store/reducers/products/tables/tables.types";
import { RuleTableResponse } from "types/table";

export interface TableSummaryDetailPanelProps {
  readonly tableId: string;
}

/**
 * Detail panel for a Table Summary row: lazy-loads the full rule table and
 * renders the full RulesTable (embedded variant) under the summary row.
 */
export function TableSummaryDetailPanel({
  tableId,
}: TableSummaryDetailPanelProps) {
  const dispatch = useDispatch<AppDispatch>();

  const tableData: RuleTableResponse | undefined = useTypedSelector(
    (state) => state.products.tables.byId[tableId]
  );
  const loadStatus: TableAsyncStatus =
    useTypedSelector(
      (state) => state.products.tables.tableLoadStatus[tableId]
    ) ?? "idle";

  useEffect(() => {
    void dispatch(fetchTableIfNeededAction(tableId));
  }, [dispatch, tableId]);

  if (tableData) {
    return (
      <PanelRoot>
        <RulesTable
          id={tableData.id}
          ruleTableData={tableData}
          variant="embedded"
        />
      </PanelRoot>
    );
  }

  if (loadStatus === "error") {
    return (
      <PanelRoot>
        <Alert severity="error">Failed to load table. Please try again.</Alert>
      </PanelRoot>
    );
  }

  return (
    <PanelRoot>
      <LoaderWrapper>
        <LoaderSpinner2 />
      </LoaderWrapper>
    </PanelRoot>
  );
}

const PanelRoot = styled.div`
  box-sizing: border-box;
  width: 100%;
  /* Extra left pad so row delete icons clear the blue inset accent. */
  padding: 12px 16px 16px 28px;
  /* Soft separator under the summary row inside the shared outline. */
  border-top: 1px solid ${(props) => props.theme.colors.tables.borders};
  overflow-x: auto;
`;

const LoaderWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 24px 0;
`;
